import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PhoneCall, Send, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PHONE_DISPLAY, PHONE_TEL } from "@/lib/site-content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_TYPES = [
  { value: "start", label: "Start new service" },
  { value: "stop", label: "Stop service" },
  { value: "move", label: "Move / Transfer service" },
  { value: "pay_bill", label: "Pay my bill" },
  { value: "manage", label: "Manage account" },
  { value: "outage", label: "Report outage" },
];

const UTILITY_TYPES = [
  { value: "electric", label: "Electricity" },
  { value: "gas", label: "Natural Gas" },
  { value: "water", label: "Water" },
  { value: "all", label: "All / Multiple" },
];

const initialState = {
  full_name: "",
  email: "",
  phone: "",
  address: "",
  city: "",
  state: "",
  zip_code: "",
  service_type: "",
  utility_type: "",
  preferred_date: "",
  notes: "",
};

export default function RequestForm() {
  const [data, setData] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k, v) => setData((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (
      !data.full_name ||
      !data.email ||
      !data.phone ||
      !data.address ||
      !data.service_type ||
      !data.utility_type
    ) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/service-requests`, data);
      setSuccess(true);
      toast.success("Request submitted! A specialist will call you shortly.");
      setData(initialState);
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please call us instead.";
      toast.error(typeof msg === "string" ? msg : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="request"
      data-testid="request-section"
      className="bg-white py-20 sm:py-28 lg:py-32 border-b border-zinc-200"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 grid lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-5">
          <p className="label-eyebrow mb-4">Request Service</p>
          <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tighter text-zinc-900">
            Prefer typing
            <br />
            <span className="text-[#FF4500]">over talking?</span>
          </h2>
          <p className="mt-6 text-zinc-600 text-base sm:text-lg leading-relaxed">
            Drop your details below — a Specpay specialist will call you within
            one business hour to confirm and complete your request.
          </p>

          <div className="mt-8 border border-zinc-200 p-6 bg-zinc-50">
            <p className="label-eyebrow mb-2">Need it now?</p>
            <a
              href={PHONE_TEL}
              data-testid="request-side-call-button"
              className="font-display font-black text-2xl sm:text-3xl text-zinc-900 hover:text-[#FF4500] transition-colors flex items-center gap-3"
            >
              <PhoneCall className="w-6 h-6 text-[#FF4500]" strokeWidth={2.7} />
              {PHONE_DISPLAY}
            </a>
            <p className="mt-2 text-sm text-zinc-500">
              Live agents · 24 hours · 7 days
            </p>
          </div>
        </div>

        <div className="lg:col-span-7">
          {success ? (
            <div
              data-testid="request-success"
              className="bg-zinc-900 text-white p-8 sm:p-12 border-t-4 border-[#FF4500] flex flex-col items-start"
            >
              <CheckCircle2 className="w-12 h-12 text-[#FF4500] mb-4" strokeWidth={2.5} />
              <h3 className="font-display font-black text-3xl sm:text-4xl tracking-tight">
                Request received.
              </h3>
              <p className="mt-3 text-zinc-300">
                A specialist will call you within one business hour. Need
                immediate help? Tap below.
              </p>
              <a
                href={PHONE_TEL}
                className="mt-6 inline-flex items-center gap-2 bg-[#FF4500] hover:bg-[#E63E00] text-white font-display font-bold px-6 py-4 transition-all active:scale-[0.97]"
              >
                <PhoneCall className="w-5 h-5" strokeWidth={2.7} />
                Call {PHONE_DISPLAY}
              </a>
              <button
                type="button"
                onClick={() => setSuccess(false)}
                className="mt-4 text-sm text-zinc-400 hover:text-white underline-offset-4 hover:underline"
              >
                Submit another request
              </button>
            </div>
          ) : (
            <form
              onSubmit={submit}
              data-testid="request-service-form"
              className="bg-white border border-zinc-200 p-6 sm:p-10"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="sm:col-span-2">
                  <Label htmlFor="full_name" className="label-eyebrow">
                    Full Name *
                  </Label>
                  <Input
                    id="full_name"
                    data-testid="form-full-name"
                    value={data.full_name}
                    onChange={(e) => update("full_name", e.target.value)}
                    placeholder="Jane Smith"
                    className="mt-2 h-12 border-zinc-300 rounded-none focus-visible:ring-[#FF4500]"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="label-eyebrow">
                    Email *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="form-email"
                    value={data.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder="jane@example.com"
                    className="mt-2 h-12 border-zinc-300 rounded-none focus-visible:ring-[#FF4500]"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="label-eyebrow">
                    Phone *
                  </Label>
                  <Input
                    id="phone"
                    data-testid="form-phone"
                    value={data.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    placeholder="(555) 123-4567"
                    className="mt-2 h-12 border-zinc-300 rounded-none focus-visible:ring-[#FF4500]"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="address" className="label-eyebrow">
                    Service Address *
                  </Label>
                  <Input
                    id="address"
                    data-testid="form-address"
                    value={data.address}
                    onChange={(e) => update("address", e.target.value)}
                    placeholder="123 Main Street"
                    className="mt-2 h-12 border-zinc-300 rounded-none focus-visible:ring-[#FF4500]"
                  />
                </div>
                <div>
                  <Label htmlFor="city" className="label-eyebrow">
                    City
                  </Label>
                  <Input
                    id="city"
                    data-testid="form-city"
                    value={data.city}
                    onChange={(e) => update("city", e.target.value)}
                    className="mt-2 h-12 border-zinc-300 rounded-none focus-visible:ring-[#FF4500]"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="state" className="label-eyebrow">
                      State
                    </Label>
                    <Input
                      id="state"
                      data-testid="form-state"
                      value={data.state}
                      onChange={(e) => update("state", e.target.value)}
                      className="mt-2 h-12 border-zinc-300 rounded-none focus-visible:ring-[#FF4500]"
                    />
                  </div>
                  <div>
                    <Label htmlFor="zip_code" className="label-eyebrow">
                      Zip
                    </Label>
                    <Input
                      id="zip_code"
                      data-testid="form-zip"
                      value={data.zip_code}
                      onChange={(e) => update("zip_code", e.target.value)}
                      className="mt-2 h-12 border-zinc-300 rounded-none focus-visible:ring-[#FF4500]"
                    />
                  </div>
                </div>
                <div>
                  <Label className="label-eyebrow">Service Type *</Label>
                  <Select
                    value={data.service_type}
                    onValueChange={(v) => update("service_type", v)}
                  >
                    <SelectTrigger
                      data-testid="form-service-type"
                      className="mt-2 h-12 border-zinc-300 rounded-none focus:ring-[#FF4500]"
                    >
                      <SelectValue placeholder="Select request" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICE_TYPES.map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-eyebrow">Utility *</Label>
                  <Select
                    value={data.utility_type}
                    onValueChange={(v) => update("utility_type", v)}
                  >
                    <SelectTrigger
                      data-testid="form-utility-type"
                      className="mt-2 h-12 border-zinc-300 rounded-none focus:ring-[#FF4500]"
                    >
                      <SelectValue placeholder="Select utility" />
                    </SelectTrigger>
                    <SelectContent>
                      {UTILITY_TYPES.map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="preferred_date" className="label-eyebrow">
                    Preferred Date
                  </Label>
                  <Input
                    id="preferred_date"
                    data-testid="form-preferred-date"
                    value={data.preferred_date}
                    onChange={(e) => update("preferred_date", e.target.value)}
                    placeholder="e.g. ASAP, Friday Dec 15, etc."
                    className="mt-2 h-12 border-zinc-300 rounded-none focus-visible:ring-[#FF4500]"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="notes" className="label-eyebrow">
                    Notes
                  </Label>
                  <Textarea
                    id="notes"
                    data-testid="form-notes"
                    value={data.notes}
                    onChange={(e) => update("notes", e.target.value)}
                    placeholder="Anything we should know?"
                    className="mt-2 min-h-[100px] border-zinc-300 rounded-none focus-visible:ring-[#FF4500]"
                  />
                </div>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:items-center">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="form-submit-button"
                  className="inline-flex items-center justify-center gap-2 bg-[#FF4500] hover:bg-[#E63E00] disabled:opacity-60 disabled:cursor-not-allowed text-white font-display font-black px-8 py-4 transition-all active:scale-[0.97]"
                >
                  {submitting ? "Submitting..." : "Submit Request"}
                  {!submitting && <Send className="w-4 h-4" strokeWidth={2.7} />}
                </button>
                <a
                  href={PHONE_TEL}
                  data-testid="form-call-button"
                  className="inline-flex items-center justify-center gap-2 border-2 border-zinc-900 text-zinc-900 hover:bg-zinc-900 hover:text-white font-display font-bold px-8 py-4 transition-all active:scale-[0.97]"
                >
                  <PhoneCall className="w-4 h-4" strokeWidth={2.7} />
                  Or call {PHONE_DISPLAY}
                </a>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
